//#region Angular Imports

import { IAngularEvent, IQService, ITimeoutService } from 'angular'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion


import { IOpObject } from '../../models/util/interfaces'
import { Enums } from '../../models/common/enums'
import { EventsNames, LocalizationHelper } from '../../helpers'

import { CreateEditResult } from '../../services/system'
import { PopupType } from '../../helpers/popup-manager'

import { IMRBaseControlDirectiveController } from './base'
import { CreateEditPopupOptions } from './create-edit-popup/create-edit-popup.directive'
import { BulkEditInitParams, CreateEditInitParams } from './create-edit-popup'


export interface CreateEditScope extends ng.IScope {
    popupOptions: DevExpress.ui.dxPopupOptions
    popupVisible: boolean

    popupType: any

    options: CreateEditPopupOptions
}

export abstract class CreateEditController extends IMRBaseControlDirectiveController {

    //#region Members

    private _moduleName: Enums.SimaxModuleName
    private _$scope: CreateEditScope
    private _$q: IQService
    private _$timeout: ITimeoutService

    private resultStatus: CreateEditResult.Status
    private resultCallback: (e: CreateEditResult<IOpObject>) => void
    private savedObject: IOpObject
    private error: any

    //#endregion

    //#region Ctor

    protected constructor(
        protected readonly referenceType: Enums.ReferenceType,
        moduleName: Enums.SimaxModuleName = null,
        $scope: CreateEditScope = null,
        $timeout: ITimeoutService = null,
        $q: IQService = null,
    ) {
        super()

        this._moduleName = moduleName;
        this._$scope = $scope;
        this._$timeout = $timeout;
        this._$q = $q;

        if (this._$scope != null) {
            this._setOptions();
            this._setEvents();
        }
    }

    //#endregion

    //#region Setup

    private _setOptions(): void {
        this._$scope.popupVisible = false
        this._$scope.options = {
            referenceType: this.referenceType,
            succsessMessage: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, this._moduleName),
            title: '',
            type: null,

            onBulkSave: (objectToSave: IOpObject) => {
                const deferred = this._$q.defer<IOpObject>();

                this.onBulkSaving(objectToSave)
                    .then(result => {
                        deferred.resolve(result)
                    }).catch(error => {
                        deferred.reject(error);
                    })

                return deferred.promise;
            },

            onSave: (objectToSave: IOpObject) => {
                const deferred = this._$q.defer<IOpObject>();

                this.onSaving(objectToSave)
                    .then(result => {
                        deferred.resolve(result)
                    }).catch(error => {
                        deferred.reject(error);
                    })

                return deferred.promise;
            },

            onBulkSaveFinished: () => {
                this.resultCallback({
                    status: CreateEditResult.Status.Success,
                    object: null,
                });
            },

            onHidden: () => {
                switch (this.resultStatus) {
                    case CreateEditResult.Status.Success: {
                        this.resultCallback({
                            status: this.resultStatus,
                            object: this.savedObject,
                        });

                        break;
                    }

                    case CreateEditResult.Status.Error: {
                        this.resultCallback({
                            status: this.resultStatus,
                            error: this.error,
                        });

                        break;
                    }

                    default: {
                        this.resultCallback({
                            status: CreateEditResult.Status.Cancelled,
                        });

                        break;
                    }
                }
            }
        }
    }

    private _setEvents(): void {
        this._$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_CREATE_EDIT_POPUP, this._moduleName),
            (event: IAngularEvent, ...args: any[]) => {
                this._initialize(args);
            }
        )

        this._$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_BULK_EDIT_POPUP, this._moduleName),
            (event: IAngularEvent, ...args: any[]) => {
                this._bulkInitialize(args);
            }
        )
    }

    private _initialize(args: any[]): void {
        if (args != null) {
            this.resultStatus = CreateEditResult.Status.Cancelled;
            this.savedObject = null;
            this.error = null;

            this.onInitialize(args[0]);
            this.resultCallback = args[1];
        }
    }

    private _bulkInitialize(args: any[]): void {
        if (args != null) {
            this.resultStatus = CreateEditResult.Status.Cancelled;
            this.savedObject = null;
            this.error = null;

            this.onBulkInitialize(args[0]);
            this.resultCallback = args[1];
        }
    }

    //#endregion

    //#region CreateEdit

    protected openCreateEdit(createEditInitParams: CreateEditInitParams): void {
        this._$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP, PopupType.CREATE_EDIT_POPUP)
        this._$timeout(() => {
            this._$scope.$broadcast(EventsNames.OPEN_CREATE_EDIT_POPUP, createEditInitParams)
        }, 100)
    }

    protected openBulkCreateEdit(bulkEditInitParams: BulkEditInitParams): void {
        this._$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP, PopupType.BULK_EDIT_POPUP)
        this._$scope.$broadcast(EventsNames.OPEN_BULK_EDIT_POPUP, bulkEditInitParams)
    }

    //#endregion

    //#region Events

    protected onBulkInitialize(bulkEditInitParams: BulkEditInitParams): void { };
    protected onInitialize(createEditInitParams: CreateEditInitParams): void { };
    protected onBulkSaving(object: IOpObject): JQueryPromise<IOpObject> { return null }
    protected onSaving(object: IOpObject): JQueryPromise<IOpObject> { return null }

    //#endregion

    //#region Result

    protected completeWithError(error: any) {
        this.error = error;
        this.resultStatus = CreateEditResult.Status.Error;
    }

    protected complete(savedObject: IOpObject) {
        this.savedObject = savedObject;
        this.resultStatus = CreateEditResult.Status.Success;
        this._$scope.$root.$broadcast(EventsNames.CLOSE_CREATE_EDIT_POPUP, savedObject)
    }

    //#endregion
}
